import { isAxiosError, type AxiosResponse } from 'axios';
import { getOperatorTasksApi } from '@/core/api/generatedApiClient';
import { rethrowHandledApiError } from '@/core/api/rethrowHandledApiError';
import { INLINE_API_ERROR_OPTIONS, type ApiRequestOptions } from '@/core/api/requestErrorMode';
import type {
    CreateOperatorTaskRequest,
    GetOperatorTaskAssigneesResponse,
    GetOperatorTaskAttentionResponse,
    GetOperatorTaskCalendarResponse,
    GetOperatorTaskHistoryResponse,
    GetOperatorTaskRelatedResponse,
    GetOperatorTaskRemindersResponse,
    GetOperatorTasksResponse,
    OperatorTaskDetailsResponse,
    OperatorTaskMutationResponse,
    OperatorTaskOptionsResponse,
    OperatorTaskReminderPreviewResponse,
    OperatorTaskSummaryResponse,
    OperatorTasksApiGetOperatorTasksRequest,
    OperatorTasksApiPreviewOperatorTaskRemindersRequest,
    OperatorTasksApiGetOperatorTaskAttentionRequest,
    OperatorTasksApiGetOperatorTaskRemindersRequest,
    OperatorTasksApiGetOperatorTaskCalendarRequest,
    UpdateOperatorTaskRequest,
} from '@/core/generated/api';

/** Сообщает об изменении задачи другим сотрудником */
export class OperatorTaskConflictError extends Error { }

/** Выполняет запрос общего задачника */
const request = async <T>(
    operation: () => Promise<AxiosResponse<T>>,
    inline = false
): Promise<T> => {
    try {
        return (await operation()).data;
    } catch (error: unknown) {
        try {
            return rethrowHandledApiError(error, inline
                ? 'inline'
                : 'default');
        } catch (handled: unknown) {
            if (isAxiosError(error) && error.response?.status === 409) {
                throw new OperatorTaskConflictError(handled instanceof Error
                    ? handled.message
                    : 'Задача изменена другим сотрудником');
            }

            throw handled;
        }
    }
};

/** Выполняет операции общего задачника */
export const operatorTaskService = {
    /** Находит действующих сотрудников для назначения ответственными */
    assignees: (searchQuery: string): Promise<GetOperatorTaskAssigneesResponse> => request(() => getOperatorTasksApi().getOperatorTaskAssignees({ searchQuery })),

    /** Загружает задачи календарного диапазона */
    calendar: (
        query: OperatorTasksApiGetOperatorTaskCalendarRequest,
        signal?: AbortSignal
    ): Promise<GetOperatorTaskCalendarResponse> => request(() => getOperatorTasksApi()
        .getOperatorTaskCalendar(query, {
            ...INLINE_API_ERROR_OPTIONS,
            signal,
            timeout: 15000
        }), true),

    /** Загружает общую очередь задач */
    list: (
        query: OperatorTasksApiGetOperatorTasksRequest,
        inline = false,
        signal?: AbortSignal
    ): Promise<GetOperatorTasksResponse> => request(() => getOperatorTasksApi()
        .getOperatorTasks(query, inline || signal
            ? {
                ...(inline
                    ? INLINE_API_ERROR_OPTIONS
                    : {}),
                signal,
                timeout: 15000
            }
            : undefined), inline),

    /** Загружает сводку смены */
    summary: (inline = false, signal?: AbortSignal): Promise<OperatorTaskSummaryResponse> => {
        const options: ApiRequestOptions = {
            ...(inline
                ? INLINE_API_ERROR_OPTIONS
                : {}),
            signal,
            timeout: 15000
        };

        return request(() => getOperatorTasksApi().getOperatorTaskSummary({}, options), inline);
    },

    /** Загружает задачи, требующие внимания, и ближайшие напоминания */
    attention: (
        query: OperatorTasksApiGetOperatorTaskAttentionRequest,
        signal?: AbortSignal
    ): Promise<GetOperatorTaskAttentionResponse> => request(() => getOperatorTasksApi()
        .getOperatorTaskAttention(query, {
            ...INLINE_API_ERROR_OPTIONS,
            signal,
            timeout: 15000
        }), true),

    /** Загружает общую ленту сохранённых напоминаний */
    reminders: (
        query: OperatorTasksApiGetOperatorTaskRemindersRequest,
        signal?: AbortSignal
    ): Promise<GetOperatorTaskRemindersResponse> => request(() => getOperatorTasksApi()
        .getOperatorTaskReminders(query, {
            ...INLINE_API_ERROR_OPTIONS,
            signal,
            timeout: 15000
        }), true),

    /** Загружает ограничения и настройки напоминаний */
    options: (): Promise<OperatorTaskOptionsResponse> => request(() => getOperatorTasksApi().getOperatorTaskOptions()),

    /** Открывает задачу */
    details: (id: string): Promise<OperatorTaskDetailsResponse> => request(() => getOperatorTasksApi().getOperatorTask({ id })),

    /** Загружает события задачи */
    history: (id: string, skipCount = 0, takeCount = 20): Promise<GetOperatorTaskHistoryResponse> => request(() => getOperatorTasksApi()
        .getOperatorTaskHistory({
            id,
            skipCount,
            takeCount
        })),

    /** Создаёт задачу в общей очереди */
    create: (data: CreateOperatorTaskRequest): Promise<OperatorTaskMutationResponse> => request(() => getOperatorTasksApi().createOperatorTask({ createOperatorTaskRequest: data })),

    /** Сохраняет изменения задачи */
    update: (id: string, data: UpdateOperatorTaskRequest): Promise<OperatorTaskMutationResponse> => request(() => getOperatorTasksApi()
        .updateOperatorTask({
            id,
            updateOperatorTaskRequest: data
        })),

    /** Отмечает выполнение задачи */
    complete: (id: string, version: string): Promise<OperatorTaskMutationResponse> => request(() => getOperatorTasksApi()
        .completeOperatorTask({
            id,
            changeOperatorTaskStatusRequest: { version }
        })),

    /** Возвращает задачу в общую очередь */
    reopen: (id: string, version: string): Promise<OperatorTaskMutationResponse> => request(() => getOperatorTasksApi()
        .reopenOperatorTask({
            id,
            changeOperatorTaskStatusRequest: { version }
        })),

    /** Показывает ближайшие напоминания задачи */
    preview: (
        query: OperatorTasksApiPreviewOperatorTaskRemindersRequest
    ): Promise<OperatorTaskReminderPreviewResponse> => request(() => getOperatorTasksApi().previewOperatorTaskReminders(query)),

    /** Находит доступные заказы для привязки */
    orders: (searchQuery: string): Promise<GetOperatorTaskRelatedResponse> => request(() => getOperatorTasksApi()
        .getOperatorTaskRelatedOrders({
            searchQuery,
            takeCount: 7
        })),

    /** Находит доступные диалоги для привязки */
    chats: (searchQuery: string): Promise<GetOperatorTaskRelatedResponse> => request(() => getOperatorTasksApi()
        .getOperatorTaskRelatedChats({
            searchQuery,
            takeCount: 7
        })),
};
